/**
 * useTableKeyboard — Keyboard navigation hook for DataTable.
 *
 * Implements roving tabindex pattern with arrow key navigation:
 * - Arrow keys move focus AND update tabindex
 * - Only one cell has tabindex="0" at any time
 * - Tab exits the table entirely
 * - Enter opens/activates the focused row
 * - Space toggles selection of the focused row
 * - Boundary cases: arrow keys at first/last row/column stop (no wrap)
 */

import type { Selection } from '@react-types/shared';
import type { RefObject } from 'react';
import { useCallback, useRef, useState } from 'react';

export interface TableKeyboardState {
  /** Current focused cell position */
  focusedCell: { rowIndex: number; colIndex: number } | null;
  /** Get tabindex for a specific cell (0 if focused, -1 otherwise) */
  getCellTabIndex: (rowIndex: number, colIndex: number) => number;
  /** Handle arrow key navigation */
  handleKeyDown: (
    e: React.KeyboardEvent,
    rowIndex: number,
    colIndex: number
  ) => void;
  /** Register a cell DOM element for focus management */
  registerCellRef: (
    rowIndex: number,
    colIndex: number,
    el: HTMLElement | null
  ) => void;
}

export interface UseTableKeyboardOptions {
  /** Total number of rows */
  rowCount: number;
  /** Total number of columns (including checkbox column if present) */
  columnCount: number;
  /** Callback when Enter is pressed on a row */
  onRowActivate?: (rowId: string) => void;
  /** Callback when Space is pressed on a row (for selection toggle) */
  onRowSelect?: (rowId: string, event: React.KeyboardEvent) => void;
  /** Get row ID by row index */
  getRowId: (rowIndex: number) => string | undefined;
  /** Current selection state */
  selection?: Selection;
  /** Whether selection is enabled */
  selectionEnabled?: boolean;
  /** Container ref to scope DOM queries to this table */
  containerRef?: RefObject<HTMLElement | null>;
}

/**
 * Hook for managing keyboard navigation in the table.
 * Implements roving tabindex pattern for efficient keyboard navigation.
 */
export function useTableKeyboard({
  rowCount,
  columnCount,
  onRowActivate,
  onRowSelect,
  getRowId,
  selectionEnabled = false,
  containerRef,
}: UseTableKeyboardOptions): TableKeyboardState {
  const [focusedCell, setFocusedCell] = useState<{
    rowIndex: number;
    colIndex: number;
  } | null>(null);

  const cellRefs = useRef<Map<string, HTMLElement | null>>(new Map());
  const focusGenerationRef = useRef(0);

  const getCellKey = (rowIndex: number, colIndex: number) =>
    `${rowIndex}-${colIndex}`;

  const registerCellRef = useCallback(
    (rowIndex: number, colIndex: number, el: HTMLElement | null) => {
      const key = getCellKey(rowIndex, colIndex);
      if (el) {
        cellRefs.current.set(key, el);
      } else {
        cellRefs.current.delete(key);
      }
    },
    []
  );

  const getCellTabIndex = useCallback(
    (rowIndex: number, colIndex: number) => {
      // If no cell is focused yet, first cell gets tabindex 0
      if (focusedCell === null) {
        return rowIndex === 0 && colIndex === 0 ? 0 : -1;
      }
      // Otherwise, only the focused cell gets tabindex 0
      return focusedCell.rowIndex === rowIndex &&
        focusedCell.colIndex === colIndex
        ? 0
        : -1;
    },
    [focusedCell]
  );

  const moveFocus = useCallback(
    (newRowIndex: number, newColIndex: number) => {
      // Boundary checks
      if (newRowIndex < 0 || newRowIndex >= rowCount) {
        return;
      }
      if (newColIndex < 0 || newColIndex >= columnCount) {
        return;
      }

      setFocusedCell({ rowIndex: newRowIndex, colIndex: newColIndex });

      // Focus the DOM element if its ref is registered.
      // For virtualized tables, scroll off-screen cells into view and wait for DOM update.
      const cellKey = getCellKey(newRowIndex, newColIndex);
      const generation = ++focusGenerationRef.current;

      // Try immediate focus first — works for cells already in the DOM.
      const immediateElement = cellRefs.current.get(cellKey);
      if (immediateElement && immediateElement.offsetParent !== null) {
        immediateElement.scrollIntoView({
          block: 'nearest',
          inline: 'nearest',
        });
        immediateElement.focus();
        return;
      }

      // Cell not yet rendered (virtualized off-screen). Scroll the row into
      // view and observe DOM mutations until the cell appears.
      //
      // Scope queries to this table's container to avoid matching rows/cells
      // from other grids on the same page.
      const scope = containerRef?.current ?? null;
      if (!scope) {
        return;
      }
      const rowElement = scope.querySelector(`[data-index="${newRowIndex}"]`);
      if (rowElement) {
        rowElement.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }

      const observer = new MutationObserver(() => {
        if (focusGenerationRef.current !== generation) {
          observer.disconnect();
          return;
        }
        const element = cellRefs.current.get(cellKey);
        if (element && element.offsetParent !== null) {
          element.focus();
          observer.disconnect();
        }
      });

      // Observe the grid element within this container (not document-level).
      const container =
        rowElement?.closest('[role="grid"]') ??
        scope.querySelector('[role="grid"]');
      if (container) {
        observer.observe(container, { childList: true, subtree: true });
        // 500ms gives virtualized renderers enough time to paint the new row.
        setTimeout(() => observer.disconnect(), 500);
      }
    },
    [rowCount, columnCount, containerRef]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, rowIndex: number, colIndex: number) => {
      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          moveFocus(rowIndex - 1, colIndex);
          break;
        case 'ArrowDown':
          e.preventDefault();
          moveFocus(rowIndex + 1, colIndex);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          moveFocus(rowIndex, colIndex - 1);
          break;
        case 'ArrowRight':
          e.preventDefault();
          moveFocus(rowIndex, colIndex + 1);
          break;
        case 'Enter': {
          e.preventDefault();
          const rowId = getRowId(rowIndex);
          if (rowId && onRowActivate) {
            onRowActivate(rowId);
          }
          break;
        }
        case ' ': {
          // colIndex=0 is the checkbox column — Space is reserved for
          // CheckboxCell's own handler, not the grid keyboard hook.
          if (selectionEnabled && colIndex !== 0) {
            e.preventDefault();
            const rowId = getRowId(rowIndex);
            if (rowId && onRowSelect) {
              onRowSelect(rowId, e);
            }
          }
          break;
        }
        case 'Home':
          e.preventDefault();
          if (e.ctrlKey || e.metaKey) {
            // Ctrl+Home: go to first cell
            moveFocus(0, 0);
          } else {
            // Home: go to first column in current row
            moveFocus(rowIndex, 0);
          }
          break;
        case 'End':
          e.preventDefault();
          if (e.ctrlKey || e.metaKey) {
            // Ctrl+End: go to last cell
            moveFocus(rowCount - 1, columnCount - 1);
          } else {
            // End: go to last column in current row
            moveFocus(rowIndex, columnCount - 1);
          }
          break;
      }
    },
    [
      moveFocus,
      getRowId,
      onRowActivate,
      onRowSelect,
      selectionEnabled,
      rowCount,
      columnCount,
    ]
  );

  return {
    focusedCell,
    getCellTabIndex,
    handleKeyDown,
    registerCellRef,
  };
}
